import { WeBuildAiAdmin } from "@webuild-ai/admin";
import { WebuildAiPlatform } from "@webuild-ai/agent-platform";

import { StructuredMention } from "@/utils/mentionParser";

/**
 * File metadata structure for upload
 */
export type FileMetadata = { name: string; type: string; size: string; id: string };

/**
 * Allowed value types for workflow invocation messages
 */
export type InvokeMessageValue =
  | string
  | string[]
  | { [k: string]: string }
  | { [k: string]: string }[]
  | StructuredMention[]
  | FileMetadata[];

/**
 * Message data structure for workflow invocation
 *
 * Supports:
 * - prompt: string
 * - files: array of file metadata objects
 * - mentions: array of structured mention objects for @mentions in chat
 * - other key-value pairs for custom workflow inputs
 */
export type InvokeMessage = {
  [k: string]: InvokeMessageValue;
};

/**
 * Type for the ChatStore's subscribe function
 */
export type SubscribeFn = (
  sseWorkflowClient: (workflowId: string, lastEventId?: string) => EventSource | null,
  adminClient: WeBuildAiAdmin,
  userId: string,
  fetchChatHistory?: (userId: string, adminClient: WeBuildAiAdmin) => Promise<void>,
) => void;

/**
 * Type for the ChatStore's invoke function
 */
export type InvokeFn = (
  adminClient: WeBuildAiAdmin,
  platformClient: WebuildAiPlatform,
  data: InvokeMessage,
) => Promise<void>;

/**
 * Parameters for subscribeAndInvoke utility
 */
export interface SubscribeAndInvokeParams {
  /** SSE client factory function */
  sseWorkflowClient: (workflowId: string) => EventSource | null;
  /** Admin API client */
  adminClient: WeBuildAiAdmin;
  /** Platform API client */
  platformClient: WebuildAiPlatform;
  /** Current user ID */
  userId: string;
  /** Callback to refresh chat history after workflow events */
  fetchChatHistory?: (userId: string, adminClient: WeBuildAiAdmin) => Promise<void>;
  /** Message data to send to the workflow */
  message: InvokeMessage;
  /** ChatStore's subscribe function */
  subscribe: SubscribeFn;
  /** ChatStore's invoke function */
  invoke: InvokeFn;
}

/**
 * Subscribes to SSE events and then invokes the workflow.
 *
 * CRITICAL: The order of operations is intentional and must not be changed.
 * subscribe() MUST be called before invoke() to prevent race conditions
 * where the backend responds before the SSE connection is established
 * (e.g., quick pre-flight checks like "Google Drive not enabled").
 *
 * Without this order:
 * 1. invoke() triggers the workflow on the backend
 * 2. Backend processes and sends SSE events immediately
 * 3. subscribe() creates EventSource AFTER events are already sent
 * 4. Events are lost, UI hangs on "On it..." indefinitely
 *
 * @example
 * ```typescript
 * await subscribeAndInvoke({
 *   sseWorkflowClient,
 *   adminClient,
 *   platformClient,
 *   userId,
 *   fetchChatHistory,
 *   message: { prompt: userMessage },
 *   subscribe,
 *   invoke,
 * });
 * ```
 */
export async function subscribeAndInvoke({
  sseWorkflowClient,
  adminClient,
  platformClient,
  userId,
  fetchChatHistory,
  message,
  subscribe,
  invoke,
}: SubscribeAndInvokeParams): Promise<void> {
  // Order is critical - subscribe MUST come first to catch all backend responses
  await subscribe(sseWorkflowClient, adminClient, userId, fetchChatHistory);
  await invoke(adminClient, platformClient, message);
}
